import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { 
  Edit, 
  CheckCircle2, 
  AlertCircle,
  FlaskConical,
  Star,
  Zap
} from 'lucide-react';
import { formatDateTime, getRoleLabel } from '../../lib/utils';
import { InfoBoxCustomFields, DetailsCustomFields, CustomFieldsSections } from '../../components/overview/CustomFieldsDisplay';

export function OverviewTab({ 
  request, 
  assignments, 
  changeRequests,
  canEditSpecialties,
  openEditSpecialtyModal,
  canEditCompany,
  openEditCompanyModal,
  isCompanyRep,
  overviewLayout,
  documents = []
}) {
  // Calculate hidden team members for Company Reps
  const getHiddenCounts = () => {
    if (!isCompanyRep()) return null;
    
    const hiddenPIs = assignments.filter(a => 
      a.status !== 'HANDED_OVER' && 
      !a.visible_to_company && 
      (a.assignment_role === 'PI' || a.assignment_role === 'INVESTIGATOR')
    ).length;
    
    const hiddenReviewers = assignments.filter(a => 
      a.status !== 'HANDED_OVER' && 
      !a.visible_to_company && 
      a.assignment_role === 'REVIEWER'
    ).length;
    
    const hiddenConsultants = assignments.filter(a => 
      a.status !== 'HANDED_OVER' && 
      !a.visible_to_company && 
      a.assignment_role === 'CONSULTANT'
    ).length;
    
    return { hiddenPIs, hiddenReviewers, hiddenConsultants };
  };

  const hiddenCounts = getHiddenCounts();
  const visibleAssignments = assignments
    .filter(a => a.status !== 'HANDED_OVER')
    .filter(a => !isCompanyRep() || a.visible_to_company);

  // Built-in optional fields default to the Details card; once explicitly configured in
  // the admin Overview layout they render wherever placed instead. Without layout data
  // (IRB / fetch failure) we stick to legacy custom-values-only rendering.
  const managedFieldIds = new Set((overviewLayout?.fields || []).map((f) => f.field_id));
  const fieldValues = overviewLayout
    ? {
        disease_condition: request.disease_condition,
        protocol_summary: request.protocol_summary,
        ...(request.custom_field_values || {}),
      }
    : (request.custom_field_values || {});

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Details Card */}
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-500">Description</Label>
            <p className="mt-1 text-slate-700">{request.description}</p>
          </div>
          {request.protocol_summary && !managedFieldIds.has('protocol_summary') && (
            <div>
              <Label className="text-slate-500">Protocol Summary</Label>
              <p className="mt-1 text-slate-700">{request.protocol_summary}</p>
            </div>
          )}
          {request.disease_condition && !managedFieldIds.has('disease_condition') && (
            <div>
              <Label className="text-slate-500">Disease/Condition</Label>
              <p className="mt-1 text-slate-700">{request.disease_condition}</p>
            </div>
          )}
          {/* Admin-configured fields placed in the Details card */}
          <DetailsCustomFields layout={overviewLayout} values={fieldValues} documents={documents} />
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(request.company_name || canEditCompany) && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-500">Company</Label>
                {canEditCompany && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={openEditCompanyModal}
                    className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                    data-testid="edit-company-btn"
                  >
                    <Edit size={12} className="mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              {request.company_name ? (
                <p className="font-medium" data-testid="overview-company-name">{request.company_name}</p>
              ) : (
                <span className="text-slate-400" data-testid="overview-company-name">Not assigned</span>
              )}
            </div>
          )}
          
          {/* Specialties */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-500">Specialties</Label>
              {canEditSpecialties && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={openEditSpecialtyModal}
                  className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                  data-testid="edit-specialties-btn"
                >
                  <Edit size={12} className="mr-1" />
                  Edit
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {request.specialty_names && request.specialty_names.length > 0 ? (
                request.specialty_names.map((name, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {name} {request.specialty_codes && `(${request.specialty_codes[idx]})`}
                  </Badge>
                ))
              ) : (
                <span className="text-slate-400">No specialties assigned</span>
              )}
            </div>
          </div>

          {/* Admin-configured custom fields placed in the Information box */}
          <InfoBoxCustomFields layout={overviewLayout} values={fieldValues} documents={documents} />
          
          {/* Linked FEA Request - only for CTA */}
          {request.type === 'CTA' && request.linked_fea_ref_no && (
            <div>
              <Label className="text-slate-500">Linked FEA</Label>
              <div className="mt-1">
                <Badge className="bg-teal-100 text-teal-700">
                  <FlaskConical className="w-3 h-3 mr-1" />
                  {request.linked_fea_ref_no}
                </Badge>
              </div>
            </div>
          )}
          
          {/* Assigned Team */}
          {assignments.filter(a => a.status !== 'HANDED_OVER').length > 0 && (
            <div>
              <Label className="text-slate-500">Assigned Team</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {/* iter119 — Detect whether Chairman auto-assigned the FO (FT) or
                    Coordinator (CTA/FEA) to all document threads. We append a ⚡ to
                    that assignee's chip so reviewers see at a glance who is auto-routed. */}
                {(() => null)()}
                {visibleAssignments.map((a) => {
                  const isFeaConsultant = (request.type === 'FEA') && (a.assignment_role === 'CONSULTANT' || a.assignment_role === 'INVESTIGATOR');
                  const isCtaInvestigator = (request.type === 'CTA') && (a.assignment_role === 'PI' || a.assignment_role === 'INVESTIGATOR');
                  // iter119 — auto-assign target detection for the ⚡ marker
                  const autoFt = request.type === 'FT' && request.ft_fo_thread_autoassign && a.assignment_role === 'FINANCIAL_OFFICER';
                  const autoCta = request.type === 'CTA' && request.coordinator_thread_autoassign && a.assignment_role === 'COORDINATOR';
                  const autoFea = request.type === 'FEA' && request.coordinator_thread_autoassign && a.assignment_role === 'COORDINATOR';
                  const isAutoAssigned = autoFt || autoCta || autoFea;
                  const piIds = request.selected_pi_user_ids || [];
                  const coiIds = request.selected_co_investigator_user_ids || [];
                  // iter166a — PI/Co-I styling is a CTA-scoped concept (it derives
                  // from the future-CTA selection made at the end of FEA). Inside
                  // an FEA workspace the same person must still read as
                  // "Consultant" — never re-label them PI / Co-I here.
                  // iter166b — Also suppress PI/Co-I styling when the row is
                  // masked: showing "PI: null / Principal Investigator" leaked
                  // the future-PI selection to the company before the user had
                  // actually approved. Masked rows fall through to the generic
                  // "<Role> (pending first approval)" pill below.
                  const isMasked = !!a.assignee_masked;
                  const isSelectedPI = !isMasked && isCtaInvestigator && piIds.includes(a.assignee_user_id);
                  const isSelectedCoI = !isMasked && isCtaInvestigator && coiIds.includes(a.assignee_user_id);
                  const hasResponded = a.status === 'DONE';
                  let chipClass = 'bg-slate-100 text-slate-700';
                  // iter166b — Default chip label honours masking. The generic
                  // role label (`Consultant`, `Investigator`, `Reviewer`, …) is
                  // suffixed with "(pending first approval)" so the company
                  // viewer never sees "Role: null".
                  let chipLabel = isMasked
                    ? `${getRoleLabel(a.assignment_role, request.type)} (pending first approval)`
                    : `${getRoleLabel(a.assignment_role, request.type)}: ${a.assignee_name}`;
                  let chipIcon = null;
                  if (isSelectedPI) {
                    chipClass = 'bg-amber-100 text-amber-800 border border-amber-300';
                    chipLabel = `PI: ${a.assignee_name}`;
                    chipIcon = <Star className="w-3 h-3 ml-1 inline" />;
                  } else if (isSelectedCoI) {
                    chipClass = 'bg-blue-100 text-blue-700 border border-blue-200';
                    chipLabel = `Co-Investigator: ${a.assignee_name}`;
                    chipIcon = <FlaskConical className="w-3 h-3 ml-1 inline" />;
                  } else if (isFeaConsultant && hasResponded && !isMasked) {
                    chipClass = 'bg-emerald-100 text-emerald-700 border border-emerald-200';
                    chipIcon = <CheckCircle2 className="w-3 h-3 ml-1 inline" />;
                  } else if (isCtaInvestigator && !isMasked) {
                    // CTA investigator who's been assigned but not yet tagged as PI or Co-I
                    chipClass = 'bg-violet-100 text-violet-800';
                    chipLabel = `Investigator: ${a.assignee_name}`;
                    if (hasResponded) chipIcon = <CheckCircle2 className="w-3 h-3 ml-1 inline" />;
                  } else if ((a.assignment_role === 'PI' || a.assignment_role === 'INVESTIGATOR') && !isMasked) {
                    chipClass = 'bg-violet-100 text-violet-800';
                    if (hasResponded) chipIcon = <CheckCircle2 className="w-3 h-3 ml-1 inline" />;
                  } else if (a.assignment_role === 'REVIEWER' && !isMasked) {
                    chipClass = 'bg-blue-100 text-blue-800';
                    if (hasResponded) chipIcon = <CheckCircle2 className="w-3 h-3 ml-1 inline" />;
                  } else if (a.assignment_role === 'COORDINATOR') {
                    // FT: the creating coordinator stays gray (no check) until they submit
                    if (request.type === 'FT' && !hasResponded) {
                      chipClass = 'bg-slate-100 text-slate-500 border border-slate-200';
                    } else {
                      chipClass = 'bg-emerald-100 text-emerald-800';
                      if (hasResponded) chipIcon = <CheckCircle2 className="w-3 h-3 ml-1 inline" />;
                    }
                  } else if (hasResponded && !isMasked) {
                    chipIcon = <CheckCircle2 className="w-3 h-3 ml-1 inline" />;
                  }
                  // iter166b — Italicise masked chips for a clear visual
                  // distinction (matches the AssignmentsTab masked row style).
                  if (isMasked) {
                    chipClass = `${chipClass} italic text-slate-500`;
                  }
                  return (
                    <Badge key={a.id} className={chipClass} data-testid={`overview-assignee-chip-${a.id}`}>
                      {chipLabel}
                      {chipIcon}
                      {isAutoAssigned && (
                        <span
                          className="inline-flex items-center ml-1 text-amber-500"
                          title="Auto-assigned to all document threads by Chairman"
                          data-testid={`overview-assignee-chip-${a.id}-auto-spark`}
                        >
                          <Zap className="w-3 h-3 fill-amber-400" />
                        </span>
                      )}
                    </Badge>
                  );
                })}
                
                {/* Hidden counts for Company Reps */}
                {hiddenCounts && (
                  <>
                    {hiddenCounts.hiddenPIs > 0 && (
                      <Badge className="bg-violet-50 text-violet-600 border border-violet-200">
                        {hiddenCounts.hiddenPIs} {request.type === 'CTA' ? 'Investigator' : 'Consultant'}(s) assigned
                      </Badge>
                    )}
                    {hiddenCounts.hiddenReviewers > 0 && (
                      <Badge className="bg-blue-50 text-blue-600 border border-blue-200">
                        {hiddenCounts.hiddenReviewers} Reviewer(s) assigned
                      </Badge>
                    )}
                    {hiddenCounts.hiddenConsultants > 0 && (
                      <Badge className="bg-teal-50 text-teal-600 border border-teal-200">
                        {hiddenCounts.hiddenConsultants} Consultant(s) assigned
                      </Badge>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Dates */}
          <div>
            <Label className="text-slate-500">Submitted</Label>
            <p className="font-medium">
              {request.submitted_at ? formatDateTime(request.submitted_at) : 'Not submitted'}
            </p>
          </div>
          {request.closed_at && (
            <div>
              <Label className="text-slate-500">Closed</Label>
              <p className="font-medium">{formatDateTime(request.closed_at)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Requests Card */}
      {/* Custom Fields Display — sections configurable from Admin → Overview Tab Layout */}
      <CustomFieldsSections layout={overviewLayout} values={fieldValues} documents={documents} />

      {changeRequests.length > 0 && (
        <ChangeRequestsCard 
          changeRequests={changeRequests} 
          request={request}
          isCompanyRep={isCompanyRep}
        />
      )}
    </div>
  );
}

function ChangeRequestsCard({ changeRequests, request, isCompanyRep }) {
  return (
    <Card className="border-orange-200 bg-orange-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-orange-800 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Change Requests
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {changeRequests.map((cr) => (
          <div 
            key={cr.id} 
            className="p-3 bg-white rounded-lg border border-orange-200" 
            data-testid={`change-request-${cr.id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-slate-900">{cr.created_by_name}</span>
              <span className="text-xs text-slate-400">{formatDateTime(cr.created_at)}</span>
            </div>
            <p className="text-slate-700 whitespace-pre-wrap">{cr.message}</p>
          </div>
        ))}
        {isCompanyRep() && request.status === 'CHANGES_REQUESTED' && (
          <p className="text-sm text-orange-700 mt-2">
            Please address the changes above and resubmit your request.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default OverviewTab;
